// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import QtyField, { QtyFieldStyles } from "@guestsupply/App/Components/Qty/QtyField";
import { AdditionalAvailabilityInfoModel } from "@guestsupply/Overrides/Components/ProductAvailability";
import { mergeWithCss } from "@guestsupply/XDL/Utilities";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";

import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    // XNG-Change
    extendedStyles?: ProductQuantityStyles;
    showLabel?: boolean;
    showDisabled?: boolean;
    //  XNG-CHANGES : To dicard accounting the price when checking disabled (for autocomplete and featured products)
    discardPrice?: boolean;
}

export interface ProductQuantityStyles {
    qtyFieldStyles?: QtyFieldStyles;
}

export const productQuantityStyles: ProductQuantityStyles = {};

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, allowZeroPricing, minimumOrderQty, detail, isVariantParent },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory, pricing, inventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    showLabel = false,
    showDisabled,
    discardPrice = false,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productQuantityStyles, extendedStyles));
    const [minQtyState, setMinQty] = useState(1);
    const [productQty, setProductQty] = useState("1");

    // XNG-CHANGES : Adding useEffect to track qty changes for variant products
    useEffect(() => {
        const minQty = Math.max(minimumOrderQty, detail?.multipleSaleQty || 1) || 1;
        const qty = Math.max(minQty, qtyOrdered);
        setProductQty(qty.toString());
        setMinQty(minQty);
        onQtyOrderedChanged?.(qty);
    }, [minimumOrderQty, detail?.multipleSaleQty]);

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${Math.max(minimumOrderQty || 1, detail?.multipleSaleQty || 1, qtyOrdered) || 1}`);
    }, [qtyOrdered]);

    // XNG-CHANGES : checking canaddtocart from additionalAvailabilityInfo messagetype (disabled if messagetype =401)
    const additionalAvailabilityInfo =
        inventory?.additionalResults?.additionalAvailabilityInfo &&
        (JSON.parse(inventory?.additionalResults?.additionalAvailabilityInfo) as AdditionalAvailabilityInfoModel);

    const disabled =
        (!hasEnoughInventory && !failedToLoadInventory) ||
        // XNG-CHANGES : To dicard accounting the price when checking disabled
        (!pricing && !discardPrice) ||
        (!allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) ||
        (!!additionalAvailabilityInfo && Number(additionalAvailabilityInfo.messageType) === 401) ||
        isVariantParent;
    if (disabled && !showDisabled && pricing) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement> | string) => {
        // XNG-CHANGES: accepting string qty values
        const value = typeof event === "string" ? event : event.target.value;

        setProductQty(value);
        if (Number(value) < (Number(minimumOrderQty) || Number(detail?.multipleSaleQty || 1))) {
            return;
        }
        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };
    // XNG-CHANGES : adding onBlur so th min value stays multipleSaleQty
    const blurHandler = () => {
        if (Number(productQty) < minQtyState) {
            setProductQty(`${minQtyState}`);
        }
    };

    // XNG-CHANGES: replcaing textfiled by QtyField component
    return (
        <QtyField
            label={labelOverride ?? translate("QTY")}
            value={productQty}
            type="qty"
            min={minQtyState}
            max={9999}
            disabled={disabled}
            showMultipleSaleQty={detail?.multipleSaleQty ? detail?.multipleSaleQty > 1 : false}
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            onBlur={blurHandler}
            showLabel={showLabel}
            extendedStyles={styles.qtyFieldStyles}
            data-test-selector="product_qtyOrdered"
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
